import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/apiClient';
import { downloadFile } from '../utils/download';
import { IconButton } from '../components/IconButton';
import { IconDownload } from '../components/icons';

interface PageEntry {
  label: string;
  to: string;
  description: string;
  tip?: string;
}

interface PageGroup {
  heading?: string;
  pages: PageEntry[];
}

interface ModuleHelp {
  key: string;
  label: string;
  blurb: string;
  pages: PageGroup[];
}

const MODULES: ModuleHelp[] = [
  {
    key: 'finance',
    label: 'Finance & Accounting',
    blurb:
      'Revenue (customers, quotations, invoices, receipts), Procurement (suppliers through to purchase orders, goods receiving and payments), Resources (assets, facilities, bookings, inventory), the General Ledger interface, Banking (accounts, deposits, reconciliation) and Budgets.',
    pages: [
      {
        heading: 'Revenue',
        pages: [
          {
            label: 'Customers',
            to: '/revenue/customers',
            description:
              'The directory of billing customers used across Revenue — company or individual details, contact information, and a running count of the quotations and invoices raised for each. Add a customer here before raising a quotation or invoice against them; the same customer record is shared by every invoice and receipt on file.',
          },
          {
            label: 'Quotations',
            to: '/revenue/quotations',
            description:
              'Quotes issued to customers for a described product or service, each with a status such as draft, sent, accepted or declined. Once a customer accepts a quotation, converting it straight into an invoice carries the line details across without re-entering them.',
            tip: 'The Convert to Invoice action only appears once a quotation’s status is Accepted.',
          },
          {
            label: 'Invoices',
            to: '/revenue/invoices',
            description:
              'Customer invoices — raised directly or converted from an accepted quotation — showing the amount due, amount paid and outstanding balance. Recording a receipt against an invoice (a Credit Sale) automatically updates its paid and outstanding status here.',
            tip: 'An invoice raised directly (not from a quotation) is receipted exactly the same way as a converted one.',
          },
          {
            label: 'Receipts',
            to: '/revenue/receipts',
            description:
              'Every payment received, whether it settles an existing invoice (Credit Sale) or is taken as an immediate payment with no invoice (Cash Sale). Each receipt records the payer, amount, payment method and type, and can be downloaded as a PDF for the payer’s records.',
            tip: 'Choose Cash Sale for a one-off payment with no invoice on file, and Credit Sale to settle an existing customer invoice.',
          },
        ],
      },
      {
        heading: 'Procurement',
        pages: [
          {
            label: 'Suppliers',
            to: '/procurement/suppliers',
            description:
              'The approved vendor register — trading name, contact details and registration information for every supplier the Fund buys from. A supplier must exist here before a purchase request or order can be raised against them, and their full purchase history is visible from their profile.',
          },
          {
            label: 'Requests',
            to: '/procurement/requests',
            description:
              'Purchase requests moving through the manager, board and chairman approval chain, each showing its current stage and who actioned it. Requests above the configured value threshold require supporting quotations to be attached before they can proceed — issue a formal RFQ to one or more suppliers directly from a draft request and record each one’s response, which counts toward that threshold the same way an uploaded quotation document does. An approved request is converted into a purchase order.',
            tip: 'A request below the value threshold skips the quotation-attachment step and moves straight through the approval chain. RFQs can only be issued and responses recorded while the request is still a draft.',
          },
          {
            label: 'Orders',
            to: '/procurement/orders',
            description:
              'Purchase orders issued to a vendor once a purchase request is approved, fixing the vendor, item and agreed amount. An order is fulfilled by recording a goods receipt against it, which in turn feeds the vendor invoice and payment.',
          },
          {
            label: 'Receiving',
            to: '/procurement/receiving',
            description:
              'Goods and services received against an issued purchase order, recording the condition and quantity received and closing out the order. Tick “Create Asset” on a capital item — a vehicle, equipment — to add it straight to the asset register and capitalise the cost instead of expensing it through Procurement.',
          },
          {
            label: 'Payments',
            to: '/procurement/payments',
            description:
              'Vendor invoices and the payment vouchers and payments raised against them, tracking what is owed to each vendor and what has already been paid. Everyday expenses that never went through a purchase order — a one-off supplier bill, a reimbursement — can be recorded here directly, with no purchase order or goods receipt required.',
            tip: 'A vendor invoice matched against its purchase order is checked automatically; a mismatch is flagged as a discrepancy rather than blocking the invoice outright.',
          },
        ],
      },
      {
        heading: 'Resources',
        pages: [
          {
            label: 'Assets',
            to: '/resources/assets',
            description:
              'The fixed asset register — asset tag, description, custodian and location for every asset the Fund owns, each shown with a status of Active, Pending Disposal, Impaired or Disposed. Assets created here or capitalised from a goods receipt carry a useful life, residual value and straight-line depreciation, with impairment or a fair-value revaluation recorded against them as needed, and photos, warranty cards or invoices attached as documents. Disposing of one is a request that emails every Resources Administrator to approve — only on approval is the asset actually marked disposed, and the requesting officer can never also approve their own request. Every change to location, department, custodian or lifecycle status is logged to the audit trail.',
            tip: 'Depreciation posts automatically to the General Ledger each month once useful life and residual value are set — nothing further to do once they’re on the record.',
          },
          {
            label: 'Facilities',
            to: '/resources/facilities',
            description:
              'The physical spaces and rooms available for use — meeting rooms, halls, vehicles — each with a current status of Active, Under Maintenance or Inactive. A facility must be Active before it can be reserved on the Bookings page.',
          },
          {
            label: 'Bookings',
            to: '/resources/bookings',
            description:
              'Reservations of a facility for a specific date and time range. Overlapping bookings for the same facility are blocked automatically at the time of booking, and cancelling a booking immediately frees the slot for someone else.',
          },
          {
            label: 'Inventory',
            to: '/resources/inventory',
            description:
              'Consumables and stock items tracked by a running quantity on hand, grouped by category such as cleaning supplies or office supplies. Stock levels change only through recorded Received, Issued or Adjusted movements rather than by editing the quantity directly, so there is always a movement history behind the current count.',
          },
        ],
      },
      {
        heading: 'General Ledger',
        pages: [
          {
            label: 'Account Mappings',
            to: '/gl/mappings',
            description:
              'Configures which Chart of Accounts account each sub-ledger transaction type — contributions, penalties, benefit payments, revenue, procurement expense — posts to. A transaction type is left out of the nightly GL feed entirely until it is mapped to an account and marked active here.',
            tip: 'A mapping saved but left inactive has no effect — its transaction type keeps missing the nightly feed until it’s explicitly activated.',
          },
          {
            label: 'Daily Batches',
            to: '/gl/batches',
            description:
              'The nightly GL batches automatically generated from every module’s transactions, one batch per day, with the posting status of each line and any exceptions that failed to post. Use this to confirm a day’s transactions reached the General Ledger, or to investigate why a particular line was rejected.',
            tip: 'A rejected line can usually be corrected and requeued from its batch detail page, rather than re-entered from scratch.',
          },
          {
            label: 'Correcting Journals',
            to: '/gl/journal-entries',
            description:
              'Manual, dual-controlled journal entries for corrections and one-off postings that don’t fit the automated feed — for example the gain or loss on an asset disposal. One user creates and posts the entry, and a second user must confirm it before it takes effect on the ledger. A posted entry can be reversed, creating a new draft with every line’s debit and credit swapped, and “New Contra Entry” gives a quick starting point for a transfer between two of the Fund’s own accounts, such as moving cash to petty cash.',
            tip: 'The officer who drafted an entry can never also be the one who posts or confirms it — have a colleague action it if you drafted it yourself.',
          },
        ],
      },
      {
        heading: 'Banking',
        pages: [
          {
            label: 'Bank Accounts',
            to: '/banking/accounts',
            description:
              'The Fund’s physical bank accounts, each linked to a General Ledger account for reconciliation. Opening an account shows its imported bank statement lines, where an unmatched line can be auto-matched against a deposit or receipt, or cleared manually.',
            tip: 'Import a bank statement as a CSV from the account’s statement lines page — auto-match pairs a line to a deposit by amount and date automatically, but every match still needs to be confirmed before it clears.',
          },
          {
            label: 'Deposits',
            to: '/banking/deposits',
            description:
              'Groups one or more receipts that were physically banked together into a single deposit slip, so the combined total can be matched against one bank statement line rather than each receipt individually. A deposit starts as a draft and is marked Deposited once it’s actually taken to the bank.',
          },
        ],
      },
      {
        heading: 'Budgets',
        pages: [
          {
            label: 'Budgets',
            to: '/budgets',
            description:
              'One budget per financial year, prepared as a set of cost-centre / GL account lines and then approved. Once approved, a purchase request checks its committed spend against the relevant line automatically. A re-appropriation moves funds between two existing lines; a supplementary adds new funds to one — both need a second Budgets Administrator’s approval before they take effect.',
            tip: 'The officer who prepared a budget (or a re-appropriation/supplementary adjustment) can never also be the one who approves it.',
          },
        ],
      },
    ],
  },
  {
    key: 'hr',
    label: 'Human Resources',
    blurb:
      'Employee records, documents and positions, plus self-service processes — leave, attendance, overtime, performance, training, payroll and separation.',
    pages: [
      {
        pages: [
          {
            label: 'Employees',
            to: '/hr/employees',
            description:
              'Staff records — personal details, employee code, current position and employment history. This is the master record every other HR page hangs off, looked up by name or employee code from documents, training, leave and payroll.',
            tip: 'Bank details recorded here (Organization tab) are what the payroll EFT export pulls from — add them before the first pay run for a new employee.',
          },
          {
            label: 'Documents',
            to: '/hr/documents',
            description:
              'Per-employee document uploads of configurable types — contracts, identification, certificates — attached to a staff member’s record. Staff can view their own uploaded documents from the self-service side, but only HR can add or remove them.',
          },
          {
            label: 'Positions',
            to: '/hr/positions',
            description:
              'The establishment — every position in the organisation, the job it is based on, its org unit, and who currently holds it, if anyone. A position must exist here before an employee can be assigned to it, and vacant positions are what feed the Recruitment pipeline.',
          },
          {
            label: 'Training',
            to: '/hr/training',
            description:
              'Training records logged per employee — course, provider, dates and outcome. Once recorded, an employee can view their own training history read-only from the self-service side.',
          },
          {
            label: 'Performance',
            to: '/hr/performance',
            description:
              'HR-drafted performance reviews, scored against the configured KPIs for a review period. A review only becomes visible to the employee once HR finalises it.',
          },
          {
            label: 'Leave',
            to: '/hr/leave',
            description:
              'Leave requests submitted by or on behalf of staff and routed to their manager for approval, alongside each employee’s entitlement and remaining balance under the configured leave policies and carry-over rules. Approving or rejecting a request here updates the employee’s balance immediately.',
          },
          {
            label: 'Attendance',
            to: '/hr/attendance',
            description:
              'Self-service clock-in and clock-out records and activity-based timesheets, each tagged with a work location — office, work-from-home or field. A batch of records can also be bulk-imported from a CSV export of a biometric, swipe or door-access system. Attendance data can be cross-checked against approved leave and overtime for the same period on the Attendance report.',
          },
          {
            label: 'Overtime',
            to: '/hr/overtime',
            description:
              'Overtime hours submitted by staff for manager approval, each costed against the configured overtime rate types such as Normal, Weekend or Public Holiday. Approved hours flow automatically into the next payroll run without needing to be re-entered.',
          },
          {
            label: 'Compensation',
            to: '/hr/compensation',
            description:
              'Each employee’s assigned pay grade and the pay components — allowances and deductions — that make up their gross and net pay. Changes made here take effect from the next payroll run created after the change.',
          },
          {
            label: 'Payroll',
            to: '/hr/payroll',
            description:
              'Pay runs taken from draft through to finalised, itemising each employee’s base pay plus approved overtime, allowances and deductions for the period, including the employee’s VNPF contribution as its own deduction line matching the amount filed on their contribution schedule. Finalising a pay run locks it and generates payslips for every employee included.',
            tip: 'Once a run is finalised, an Export EFT CSV button appears with net pay and bank details ready to take to the bank.',
          },
          {
            label: 'Payslips',
            to: '/hr/payslips',
            description:
              'Payslips generated automatically once a pay run is finalised, one per employee per run. Staff can view and download their own payslips from the self-service side.',
          },
          {
            label: 'Recruitment',
            to: '/hr/recruitment',
            description:
              'Open vacancies and the candidates applying for them, moving through configurable pipeline stages such as Applied, Interview and Offer. Attach a CV to a candidate’s record and track their stage as they progress toward an offer or rejection.',
          },
          {
            label: 'Separation',
            to: '/hr/separation',
            description:
              'Exit records for departing employees, with a configurable completion checklist — return of equipment, handover, exit interview — that must be worked through before the separation is finalised. A final pay amount is recorded and the record is locked once finalised.',
          },
        ],
      },
    ],
  },
  {
    key: 'investments',
    label: 'Investment Management',
    blurb:
      'A register of every investment the Fund holds — property, equity, loans, fixed interest, cash and offshore — each with an auto-generated reference code, valuation history, income tracking and a full change log, checked against policy allocation ranges.',
    pages: [
      {
        pages: [
          {
            label: 'Policy',
            to: '/investments/policy',
            description:
              'The Fund’s target allocation range — a minimum and maximum percentage — for each investment asset class such as property, equity, loans, fixed interest, cash and offshore. Any breach of these ranges is flagged automatically on the Investment Management Dashboard and the Asset Allocation report.',
          },
          {
            label: 'Investments',
            to: '/investments',
            description:
              'The register of every investment holding the Fund has made — auto-generated reference code, asset class, currency, amount invested and counterparty, plus asset-class-specific fields such as lease dates for property or coupon rate for fixed interest. A holding’s detail page also records its valuation history, expected and actual income, attached documents, and a full change log. Property and Fixed Interest can generate their own future rent or coupon schedule; Loans get a full repayment schedule with interest-then-principal payment allocation, an NPL flag and a coverage ratio; Equity holdings log corporate actions (bonus and rights issues, buybacks) that automatically adjust units held; and a Cash & Term Deposit can be rolled over into a new investment at maturity. Disposing of a holding, writing off a loan, or restructuring one always needs a different Investment Administrator from whoever made the change to approve it.',
            tip: 'The Counterparty picker only appears for asset classes that use one — Property (tenant) and Loans (borrower).',
          },
          {
            label: 'Counterparties',
            to: '/investments/counterparties',
            description:
              'The register of tenants, borrowers and other parties an investment can be linked to — for example a property’s tenant or a loan’s borrower. Add a counterparty here before selecting them on an investment’s record.',
          },
        ],
      },
    ],
  },
  {
    key: 'memberships',
    label: 'Memberships',
    blurb:
      "The Fund's member register, employers and their contribution schedules, penalties, statements, and benefit claims through to payment.",
    pages: [
      {
        pages: [
          {
            label: 'Members',
            to: '/memberships',
            description:
              'The Fund’s member register — personal details, member type and a live view of each member’s contributions and statements. If a member has an email address, a self-service portal login is created for them automatically, or linked to an existing account sharing that email.',
          },
          {
            label: 'Employers',
            to: '/memberships/employers',
            description:
              'Registered employers who contribute on behalf of members, with their registration details and a list of the members they contribute for. An employer must be registered here before a contribution schedule can be submitted for them.',
          },
          {
            label: 'Contribution Schedules',
            to: '/memberships/contribution-schedules',
            description:
              'Monthly schedules submitted by an employer, listing the employer and employee contribution amount for each of their members. Once a schedule is submitted it is receipted, crediting each listed member’s contribution balance.',
          },
          {
            label: 'Penalties & Surcharges',
            to: '/memberships/penalties',
            description:
              'Late-payment penalties and surcharges raised against an employer for overdue contributions. An employer or officer can request a waiver, which is auto-approved below the configured threshold and otherwise routed to a senior approver.',
          },
          {
            label: 'Statements',
            to: '/memberships/statements',
            description:
              'Mini, detailed and annual contribution statements generated for any member on demand, each downloadable as a PDF. A detailed statement lists every contribution transaction; the annual statement summarises a full calendar year.',
          },
          {
            label: 'Claims',
            to: '/memberships/benefits/claims',
            description:
              'Benefit claims — withdrawal, death, or another configured benefit type — submitted by or on behalf of a member. Each claim is assessed and moves through its configured approval chain before it is approved for payment.',
          },
          {
            label: 'Payment Batches',
            to: '/memberships/benefits/payment-batches',
            description:
              'EFT batches grouping approved benefit payments together for disbursement, with each line showing the recipient’s bank details and its individual disbursement status. A payment only leaves this stage once its batch is processed.',
          },
          {
            label: 'Cheques',
            to: '/memberships/benefits/cheques',
            description:
              'Cheques issued as an alternative to EFT for approved benefit claims, tracked through e-signature collection to final issue status. Use this when a recipient has no bank account registered for an EFT payment batch.',
          },
        ],
      },
    ],
  },
  {
    key: 'communications',
    label: 'Communications',
    blurb: 'Staff-wide announcements and a shared events calendar — both found under System in the left menu.',
    pages: [
      {
        pages: [
          {
            label: 'Announcements',
            to: '/announcements',
            description:
              'Staff-wide notices with a priority level of Normal or Important and an optional expiry date. Active, unexpired announcements appear automatically at the top of every user’s Home page.',
          },
          {
            label: 'Events',
            to: '/events',
            description:
              'A shared calendar of events — meetings, training sessions, public holidays — each with a location, category and status. Events marked as public holidays are also taken into account by Attendance and Leave calculations.',
          },
        ],
      },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    blurb:
      'A report for every register across every module, filterable by date and other criteria, and exportable as a PDF or CSV. Reports are grouped by module on the Reports page.',
    pages: [
      {
        pages: [
          {
            label: 'Consolidated',
            to: '/reports',
            description:
              'The Fund’s core financial statements: Trial Balance, Income Statement, Balance Sheet, Statement of Changes in Fund Balance, Statement of Cash Flows (direct or indirect method), a Comprehensive Income statement, and a Ratio Analysis report of liquidity, solvency and fund-health ratios for Board reporting — each runnable as at or over any date range with optional comparative-period figures.',
            tip: 'On the Trial Balance, click an account code to drill straight into that account’s full transaction history.',
          },
          {
            label: 'General Ledger Interface',
            to: '/reports',
            description:
              'Reports over the automated GL feed itself — the Daily Interface Log of every batch posted, GL Exceptions for lines that were rejected, the monthly Sub-ledger to GL Reconciliation, the Account Mapping report, the Correcting Journals Register of manual entries, and the General Ledger report — every posted transaction for one account, with a running balance, also reachable by clicking an account code on the Trial Balance.',
          },
          {
            label: 'Memberships',
            to: '/reports',
            description:
              'Member Directory, New Members, Member Demographics, the Employer Register, and the Contribution Schedules Register — the core membership and employer reporting set.',
          },
          {
            label: 'Benefit Payments',
            to: '/reports',
            description:
              'Benefit Claims in Progress by Stage, showing where every open claim currently sits in the approval chain, plus the Benefit Claims Register, Benefit Payment Batches Register and Benefit Cheque Register.',
          },
          {
            label: 'Penalties & Surcharges',
            to: '/reports',
            description:
              'The Penalty Ageing Schedule, bucketing every outstanding penalty by how many days overdue it is — the primary tool for chasing unpaid penalties by age.',
          },
          {
            label: 'Revenue',
            to: '/reports',
            description:
              'Receipts, Quotation Register, Invoice Register & Aging, an Accounts Receivable Ageing report bucketing every outstanding customer invoice by days overdue, Receipts & Collections Summary, Revenue by Category, and the Customer Register.',
          },
          {
            label: 'Procurement',
            to: '/reports',
            description:
              'Purchase Request Pipeline, Purchase Order Register, Goods Receiving Log, Vendor Payments, an Accounts Payable Ageing report bucketing every outstanding vendor invoice by days overdue, Supplier Register, a Spend Analysis by vendor, and a Vendor Performance scorecard (on-time delivery rate and invoice discrepancy rate) — covering a purchase from request through to payment.',
          },
          {
            label: 'Resources',
            to: '/reports',
            description:
              'Asset Register — with net book value and accumulated depreciation — Depreciation Schedule, an Asset Movement Log of every logged location, custodian, disposal, impairment and revaluation change, Facility Directory, Inventory Stock Levels, Inventory Movement Log, and Bookings Register.',
          },
          {
            label: 'Human Resources',
            to: '/reports',
            description:
              'The full HR reporting set: Attendance, Leave and Leave Balances, Payroll, Overtime, Recruitment Pipeline, Performance Reviews, Training Records, Employee Documents, Separation, Employee Register, Positions/Establishment, and a Turnover & Attrition report of monthly headcount, separations and turnover rate.',
          },
          {
            label: 'Investment Management',
            to: '/reports',
            description:
              'The Investment Register (also exportable to Excel), the Asset Allocation report checking current holdings against each asset class’s policy range with a trend history and a what-if reallocation calculator, an Income vs Expected report ageing overdue investment income, and the Investment Change Log — every logged change to an investment record, with who made it. Plus a report per asset class: Loan Portfolio & Amortisation (outstanding principal, next installment, effective yield, NPL flag), Fixed Interest Maturity Profile (YTM, duration, maturity bucket), Property Lease & Expiry Register (lease and insurance expiry, gross yield), Cash & Liquidity (accrued interest, idle-cash flag, portfolio liquidity ratio), and Equity Holdings & Concentration and Offshore Exposure (NAV, total return, and a concentration flag over 25% of that asset class).',
          },
          {
            label: 'Announcements',
            to: '/reports',
            description:
              'The Announcement Log of every notice ever posted, and Active Announcements currently showing on the Home page.',
          },
          {
            label: 'Events',
            to: '/reports',
            description: 'The Event Calendar report, listing every event — including public holidays — over a chosen date range.',
          },
          {
            label: 'Banking',
            to: '/reports',
            description:
              'Bank Reconciliation — the balance per the General Ledger against cleared bank statement items, with every unmatched or uncleared line listed.',
          },
          {
            label: 'Budgets',
            to: '/reports',
            description:
              'Committed Budget (revised budget, committed obligations and remaining balance per line) and Budget Variance (revised budget, actual spend, and variance per line).',
          },
          {
            label: 'Custom',
            to: '/reports',
            description:
              'Report Packs — bundle several individual reports together into a single combined PDF, useful for a monthly board pack or an audit request.',
          },
        ],
      },
    ],
  },
  {
    key: 'system',
    label: 'System',
    blurb:
      'Platform administration — user accounts and roles, the chart of accounts, and the configuration lists and policies the other modules run on.',
    pages: [
      {
        pages: [
          {
            label: 'Accounting Periods',
            to: '/system/accounting-periods',
            description:
              'Financial periods that are explicitly opened and closed. Once a period is closed, any transaction dated inside it is rejected everywhere in the system — the control that prevents back-dated postings into a period that has already been reported on.',
          },
          {
            label: 'Audit Log',
            to: '/system/audit-log',
            description:
              'A change log of every logged create, update or delete across the records that carry audit tracking — currently Assets, Chart of Accounts, Journal Entries, Investments, Employees, User accounts, and Financial Years and Accounting Periods — showing who changed what and the old and new values. Sensitive fields such as a user’s password are never logged, but a changed bank/payout detail on an Employee record is.',
            tip: 'Filter by Log to narrow the list down to just one record type, such as investment or asset.',
          },
          {
            label: 'Authentication',
            to: '/system/authentication',
            description:
              'Sign-in options for the whole portal — password complexity rules, whether two-factor authentication is required, and any single sign-on providers configured.',
          },
          {
            label: 'Benefit Approval Levels',
            to: '/memberships/benefits/approval-levels',
            description:
              'Defines the approval chain a benefit claim must pass through before payment, configured per benefit type and by claim amount — for example, a small withdrawal may need one approver while a large death benefit needs several.',
          },
          {
            label: 'Benefit Plans',
            to: '/system/benefit-plans',
            description:
              'The benefit plans members can belong to, and which benefit types each plan covers. A member’s plan determines which benefit types they are eligible to claim under Memberships > Claims.',
          },
          {
            label: 'Benefit Providers',
            to: '/system/benefit-providers',
            description:
              'External providers — insurers, fund managers — that a benefit payment can be routed to instead of paying the member directly.',
          },
          {
            label: 'Benefit Types',
            to: '/system/benefit-types',
            description:
              'The kinds of benefit a member can claim — withdrawal, death, and any others configured — and the eligibility rules attached to each.',
          },
          {
            label: 'Chart of Accounts',
            to: '/system/chart-of-accounts',
            description:
              'The General Ledger’s account tree, organised into a parent/child hierarchy. Every account mapping, manual journal entry and financial statement in the system is built from this tree, so a new account must be added here before it can be posted to or mapped.',
          },
          {
            label: 'Exchange Rates',
            to: '/system/exchange-rates',
            description: 'Currency exchange rates used whenever a foreign-currency amount needs converting to the Fund’s base currency.',
          },
          {
            label: 'Jobs',
            to: '/system/jobs',
            description: 'The job catalogue — job titles and descriptions — that HR positions in Positions/Establishment are created from.',
          },
          {
            label: 'Leave Carry-Over Rules',
            to: '/system/leave-carry-over-rules',
            description:
              'How many days of unused leave, per leave type, an employee is allowed to carry into the following year rather than forfeiting.',
          },
          {
            label: 'Leave Entitlement Policies',
            to: '/system/leave-entitlement-policies',
            description:
              'How many days of each leave type staff are entitled to per year, which can vary by policy — for example by employee grade or length of service.',
          },
          {
            label: 'Lists',
            to: '/system/lists',
            description:
              'The option values that populate dropdowns throughout the portal — statuses, categories, types — grouped by list. This is the general-purpose configuration screen for any small set of choices that isn’t its own dedicated page, such as Benefit Types or Jobs.',
          },
          {
            label: 'Org Units',
            to: '/system/org-units',
            description:
              'The organisational structure — departments and units — that every HR position belongs to, used for grouping and reporting on the establishment. The hierarchy can be exported as a PDF organisation chart at any time.',
          },
          {
            label: 'Overtime Rate Types',
            to: '/system/overtime-rate-types',
            description:
              'The overtime rate categories — Normal, Weekend, Public Holiday and so on — and the multiplier applied to base pay for each, used when overtime is costed into payroll.',
          },
          {
            label: 'Pay Components',
            to: '/system/pay-components',
            description:
              'The allowances and deductions available to attach to an employee’s compensation — for example a housing allowance or a loan deduction.',
          },
          {
            label: 'Pay Grades',
            to: '/system/pay-grades',
            description: 'The salary bands employees can be placed on, each setting the pay range a position at that grade falls within.',
          },
          {
            label: 'Performance KPIs',
            to: '/system/performance-kpis',
            description:
              'The key performance indicators that HR performance reviews are scored against, shared across every review so scores are comparable between staff. Can optionally be grouped under a Performance KRA.',
          },
          {
            label: 'Performance KRAs',
            to: '/system/performance-kras',
            description:
              'Key Result Areas — an optional grouping layer above Performance KPIs, each with its own weight. A grouped KPI\'s weight is normalized within its KRA rather than across the whole review.',
          },
          {
            label: 'Competencies',
            to: '/system/competencies',
            description:
              'The organization-wide behavioral competencies (Communication, Integrity, Teamwork…) every review is also scored against, shown as a separate figure alongside the KPI/KRA-based score.',
          },
          {
            label: 'Performance Review Policies',
            to: '/system/performance-review-policies',
            description: 'How often performance reviews are due — for example annually — and how they are scored, applied when HR drafts a new review.',
          },
          {
            label: 'Separation Checklist Items',
            to: '/system/separation-checklist-items',
            description:
              'The checklist items — return company equipment, complete handover, exit interview — that must all be ticked off before an employee’s separation record can be finalised.',
          },
          {
            label: 'Settings',
            to: '/system/settings',
            description:
              'Organisation-wide configuration: name and icon, base currency, timezone, the document number prefix used for each module — invoices, receipts, purchase orders, assets, and so on — and procurement approval thresholds.',
          },
          {
            label: 'Templates',
            to: '/system/templates',
            description:
              'The email and message templates the system sends automatically — leave approval, benefit payment notifications — editable with placeholders that are filled in with real data when the message is sent.',
          },
          {
            label: 'Work Shifts',
            to: '/system/work-shifts',
            description:
              'Shift patterns — start and end times, days — that attendance clock-ins and overtime are measured against for staff assigned to a shift.',
          },
          {
            label: 'Users',
            to: '/users',
            description:
              'Portal accounts for every staff, member, supplier, customer and employer login. Module roles — which modules and actions a user can perform — are assigned on the Permissions tab; self-service roles (member, supplier, customer, employer) are assigned on the Roles tab.',
            tip: 'Module roles and self-service roles live on two different tabs of the same user — it’s easy to add one and miss the other.',
          },
        ],
      },
    ],
  },
];

interface SearchHit {
  moduleLabel: string;
  groupHeading?: string;
  page: PageEntry;
}

const SEARCH_INDEX: SearchHit[] = MODULES.flatMap((m) =>
  m.pages.flatMap((g) => g.pages.map((page) => ({ moduleLabel: m.label, groupHeading: g.heading, page })))
);

export function Help() {
  const [active, setActive] = useState(MODULES[0].key);
  const [search, setSearch] = useState('');
  const currentModule = MODULES.find((m) => m.key === active) ?? MODULES[0];
  const download = () => downloadFile(api, '/help/pdf', 'help.pdf');

  const query = search.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!query) return [];
    return SEARCH_INDEX.filter(({ moduleLabel, groupHeading, page }) =>
      [moduleLabel, groupHeading, page.label, page.description, page.tip].some((text) => text?.toLowerCase().includes(query))
    );
  }, [query]);

  const renderPage = (page: PageEntry, breadcrumb?: string) => (
    <details className="help-page" key={page.to + page.label}>
      <summary>
        {page.label}
        {breadcrumb && <span className="help-page-breadcrumb">{breadcrumb}</span>}
      </summary>
      <div className="help-page-body">
        <p>{page.description}</p>
        {page.tip && <p className="help-page-tip">{page.tip}</p>}
        <Link to={page.to}>Open {page.label} →</Link>
      </div>
    </details>
  );

  return (
    <>
      <header className="page-header">
        <h1>Help</h1>
        <div className="page-header-actions">
          <IconButton icon={IconDownload} label="Download as PDF" onClick={download} />
        </div>
      </header>
      <p className="muted">What each page does, grouped by module — or search for a page below.</p>

      <div className="toolbar">
        <input
          type="search"
          className="help-search"
          placeholder="Search for a page, e.g. “receipts” or “leave”…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {query ? (
        <div className="card changelog-release">
          <p className="muted help-search-count">
            {searchResults.length} {searchResults.length === 1 ? 'page' : 'pages'} matching “{search}”
          </p>
          {searchResults.length === 0 ? (
            <p className="muted">No pages found. Try a different word, or clear the search to browse by module.</p>
          ) : (
            searchResults.map(({ moduleLabel, groupHeading, page }) =>
              renderPage(page, groupHeading ? `${moduleLabel} › ${groupHeading}` : moduleLabel)
            )
          )}
        </div>
      ) : (
        <>
          <div className="tab-group">
            {MODULES.map((m) => (
              <button
                key={m.key}
                type="button"
                className={active === m.key ? 'tab active' : 'tab'}
                onClick={() => setActive(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>

          <p className="muted help-module-blurb">{currentModule.blurb}</p>

          <div className="card changelog-release">
            {currentModule.pages.map((group, i) => (
              <div key={group.heading ?? i}>
                {group.heading && <h3 className="help-page-group">{group.heading}</h3>}
                {group.pages.map((page) => renderPage(page))}
              </div>
            ))}
          </div>
        </>
      )}

      <p className="muted help-footer-note">
        Looking for something else? Visit the <Link to="/support">Support</Link> page, or see
        what's changed recently in the <Link to="/changelog">Change Log</Link>.
      </p>
    </>
  );
}
